import { useState } from "react";
import { OrderType } from "./useTradingForm";
import { useCryptoTrade } from "../api/trading/useCryptoTrade";

interface UseTradeExecutionProps {
  amount: string;
  limitOrderPrice: string;
  triggerPrice: string;
  portfolioShortName: string;
  contactId: string;
  securityCode: string;
  currency: string;
  orderType: OrderType;
  tradeType: "units" | "amount";
  endTime: string;
  unitPriceWithSlippage: string;
  actionType: "buy" | "sell";
}

const getTransactionTypeCode = (actionType: "buy" | "sell") => {
  if (actionType === "buy") {
    return "B";
  } else if (actionType === "sell") {
    return "S";
  } else {
    // Throw error for safety
    throw new Error("Invalid action type");
  }
};

export const useTradeExecution = ({
  amount,
  limitOrderPrice,
  triggerPrice,
  portfolioShortName,
  contactId,
  securityCode,
  currency,
  orderType,
  tradeType,
  endTime,
  unitPriceWithSlippage,
  actionType,
}: UseTradeExecutionProps) => {
  const [tradeError, setTradeError] = useState("");

  const { handleTrade, submitting: isSubmitting } = useCryptoTrade({
    portfolioShortName,
    units: Number(amount),
    unitPrice:
      orderType === "Limit" || orderType === "StopLimit"
        ? limitOrderPrice
        : unitPriceWithSlippage,
    transactionTypeCode: getTransactionTypeCode(actionType),
    contact: contactId,
    securityCode,
    currency,
    dryRun: false,
    orderType: orderType === "StopLimit" ? "Limit" : orderType,
    ...(orderType === "StopLimit" && { strategy: "StopLimit" }),
    ...(orderType === "StopLimit" && { triggerPrice: triggerPrice }),
    ...(actionType === "buy" &&
      tradeType === "amount" && { unitAsset: "quote" }),
    ...(orderType === "Limit" &&
      endTime && { endTime: new Date(endTime).getTime() }),
  });

  const executeTrade = async (): Promise<TradeExecutionResponse> => {
    setTradeError("");
    try {
      const response = await handleTrade();
      if (response.status === "executed") {
        return { status: "executed", response };
      } else if (response.status === "accepted") {
        return { status: "accepted", response };
      } else {
        setTradeError(response.message);
        return { status: "error", message: response.message };
      }
    } catch (error) {
      setTradeError("An unexpected error occurred");
      return { status: "error", message: "An unexpected error occurred" };
    }
  };

  return {
    executeTrade,
    isSubmitting,
    tradeError,
    resetTradeState: () => {
      setTradeError("");
    },
  };
};

export type SuccessfulTradeResponse = {
  status: "executed" | "accepted";
  response: unknown;
};

export type FailedTradeResponse = {
  status: "error";
  message: string;
};

export type TradeExecutionResponse =
  | SuccessfulTradeResponse
  | FailedTradeResponse;
